import {
  Text,
  Box,
  Stack,
  Flex,
  useColorModeValue,
  FormControl,
  Collapse,
  Tooltip,
  Hide,
  FormLabel,
  Show,
  Switch,
  Divider,
} from '@chakra-ui/react';
import { MeContext } from '../../../context/MeContext';
import { useNavigate } from 'react-router-dom';
import {
  CustomReactSelect,
  formatOptionLabelFilterPassTemplate,
} from '../../common/CustomReactSelect';
import { useContext, useEffect, useRef, useState } from 'react';
import { HttpContext } from '../../../context/HttpContext';
import sentry from '../../../sentry';
import { useForm } from 'react-hook-form';
import PassDownloadTemplateContext from '../../../store/client/PassDownloadTemplateContext';
import CustomSearchableSelect from '../../common/CustomSearchableSelect';
import { fetchPassTemplates } from '../../../services/passTemplatesService';
import { CustomExclamationMarkInverseIcon } from '../../../theme/icons/CustomExclamationMarkInverseIcon';

const PassTargetSection = ({
  isEdit,
  defaultEntityPage,
  setDefaultEntityPage,
  selectedPassTemplates,
  setSelectedPassTemplates,
}) => {
  const { authAxios } = useContext(HttpContext);
  const passDownloadCtx = useContext(PassDownloadTemplateContext);
  const meCtx = useContext(MeContext);
  const { filterEntity, entities } = meCtx.state;
  const [filterEntityValue, setFilterEntityValue] = useState('');
  const [entitiesList, setEntitiesList] = useState([]);
  const [passTemplatesList, setPassTemplatesList] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [disableSwitch, setDisableSwitch] = useState(false);
  const bgTooltip = useColorModeValue('secondaryDark', '#dddddd');
  const textTooltip = useColorModeValue('#ffffff', 'primaryDark');
  const modeFormBoxBg = useColorModeValue('gradient', 'secondaryDark');
  const modeTextBg = useColorModeValue('quaternaryBackground', 'primaryDark06');
  const modeLabelBg = useColorModeValue('#EFEFEF', 'primaryDark03');
  const modeBorderColor = useColorModeValue('#D9D9D9', 'primaryDark03');
  const filterEntityRef = useRef(filterEntity);
  const navigate = useNavigate();

  const { register, trigger, watch, setValue } = useForm({
    shouldUnregister: true,
    defaultValues: {
      byPassTemplate: false,
      byEntity: false,
      defaultEntityPage: false,
      passTemplate: null,
      entity: null,
      pageName: null,
    },
  });

  const watchByEntity = watch('byEntity');
  const watchEntity = watch('entity');
  const watchDefaultEntityPage = watch('defaultEntityPage');

  const determineEntity = async () => {
    let selectedEntity;

    if (meCtx.state.entities?.length === 1) {
      // only one entity
      selectedEntity = meCtx.state.entities[0];
    } else if (filterEntity) {
      // filterEntity is set
      selectedEntity = filterEntity;
    } else if (passDownloadCtx.entities?.length > 0) {
      // default entity page
      const matchedEntity = meCtx.state.entities.find(
        (entity) => entity.uuid === passDownloadCtx.entities[0]
      );
      selectedEntity = matchedEntity;
    } else if (
      !filterEntity &&
      passDownloadCtx.entities?.length === 0 &&
      passDownloadCtx.passTemplates?.length > 0
    ) {
      let passTemplate;
      let entityUuid;
      if (isEdit) {
        const firstPassTemplateUuid = passDownloadCtx.passTemplates[0];
        passTemplate = await authAxios.get(
          `passes/templates/${firstPassTemplateUuid}`
        );
        entityUuid = passTemplate.data?.data?.templateEntity;
        selectedEntity = meCtx.state.entities.find(
          (entity) => entity.uuid === entityUuid
        );
      } else {
        const firstPassTemplateId = passDownloadCtx.passTemplates[0];
        const passTemplates = await authAxios.get(
          `passes/templates?fields=id,uuid,entityId`
        );
        passTemplate = passTemplates.data?.data.find(
          (template) => template.id === firstPassTemplateId
        );
        const entityId = passTemplate.entity_id;
        selectedEntity = meCtx.state.entities.find(
          (entity) => entity.id === entityId
        );
      }
    }

    // fallback
    if (!selectedEntity && meCtx.state.entities.length > 0) {
      selectedEntity = meCtx.state.entities[0];
    }

    setSelectedEntity(selectedEntity);

    setValue('entity', {
      label: selectedEntity.name,
      value: selectedEntity,
    });
    setValue('byEntity', true);
    setValue('pageName', `${selectedEntity.name} page`);

    const isDefaultPage = passDownloadCtx.entities?.includes(
      selectedEntity.uuid
    );
    setValue('defaultEntityPage', isDefaultPage);
    setDefaultEntityPage(isDefaultPage);

    return selectedEntity;
  };

  useEffect(() => {
    if (defaultEntityPage) {
      passDownloadCtx.updateEntities([
        ...passDownloadCtx.entities,
        selectedEntity.id,
      ]);
    }
  }, [defaultEntityPage, selectedEntity]);

  useEffect(() => {
    if (selectedEntity && selectedEntity.pass_download_template_id) {
      setDisableSwitch(true);
    } else {
      setDisableSwitch(false);
    }
  }, [selectedEntity]);

  useEffect(() => {
    if (entities) {
      console.log('entities', entities);
      let filterEntityValue;
      if (filterEntity) {
        filterEntityValue = entities.find(
          (entity) => entity.id === filterEntity.id
        );
      }
      const initialEntity =
        entities.length > 1 ? filterEntityValue : entities[0];
      setFilterEntityValue(initialEntity);

      setValue('passTemplate', null);
      setValue('byPassTemplate', false);
      setValue('defaultEntityPage', false);
      setValue('pageName', null);

      if (!isEdit && initialEntity) {
        const selected = {
          label: initialEntity.name,
          value: initialEntity,
        };
        setSelectedEntity(initialEntity);
        setValue('entity', selected);
        setValue('byEntity', true);
        setValue('pageName', `${initialEntity.name} page`);
      } else {
        setValue('byEntity', false);
        setValue('entity', null);
      }
    }
  }, [entities, filterEntity, isEdit, setValue]);

  // useEffect(() => {
  //   if (filterEntity) {
  //     console.log('filterEntity', filterEntity);
  //     console.log('entitiesList', entitiesList);
  //     setValue('entity', {
  //       label: filterEntity.name,
  //       value: filterEntity,
  //     });
  //   }
  // }, [filterEntity, entities, setValue]);

  useEffect(() => {
    let entitiesArray = [];
    if (entities) {
      [...entities]
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach((item) => {
          entitiesArray.push({
            label: item.name,
            value: item,
          });
        });
    }
    setEntitiesList([...entitiesArray]);
  }, [entities]);

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        const response = await authAxios.get(`accounts/me`);
        const entities = response?.data?.entities;
        let entitiesArray = [];
        if (entities) {
          if (filterEntityValue) {
            const filteredEntity = entities.filter(
              (item) => item.id === filterEntityValue.id
            );
            if (filteredEntity.length > 0) {
              setEntitiesList([
                {
                  label: filteredEntity[0].name,
                  value: filteredEntity[0],
                },
              ]);
              return;
            }
          }
          entities.sort((a, b) => a.name.localeCompare(b.name));
          entities.forEach((item) => {
            entitiesArray.push({
              label: item.name,
              value: item,
            });
          });
        }
        setEntitiesList([...entitiesArray]);
      } catch (error) {
        sentry.sendError(error);
        console.log(error);
      }
    };
    fetchEntities();
  }, [authAxios, filterEntityValue]);

  useEffect(() => {
    const getPassTemplates = async () => {
      setSelectedPassTemplates([]);
      try {
        const { passTemplatesList, selectedTemplates } =
          await fetchPassTemplates(
            authAxios,
            filterEntity ? filterEntity : selectedEntity,
            null,
            passDownloadCtx.passTemplates
          );
        setPassTemplatesList(passTemplatesList);
        setSelectedPassTemplates(selectedTemplates);
      } catch (error) {
        console.error('Error fetching pass templates:', error);
      }
    };
    if (!isEdit && passDownloadCtx) {
      getPassTemplates();
    }
  }, [
    authAxios,
    filterEntity,
    selectedEntity,
    passDownloadCtx.passTemplates,
    isEdit,
  ]);

  useEffect(() => {
    if (isEdit) {
      if (filterEntity !== filterEntityRef.current) {
        filterEntityRef.current = filterEntity;
        navigate('/pass-download-pages');
      }
    }
  }, [filterEntity]);

  useEffect(() => {
    const getPassTemplates = async () => {
      setSelectedPassTemplates([]);

      const selectedEntity = await determineEntity();

      try {
        const { passTemplatesList, selectedTemplates } =
          await fetchPassTemplates(
            authAxios,
            selectedEntity,
            null,
            passDownloadCtx.passTemplates
          );
        setPassTemplatesList(passTemplatesList);
        setSelectedPassTemplates(selectedTemplates);
      } catch (error) {
        console.error('Error fetching pass templates:', error);
      }
    };

    if (isEdit && passDownloadCtx) {
      getPassTemplates();
    }
  }, [authAxios, filterEntity, passDownloadCtx.passTemplates, isEdit]);

  useEffect(() => {
    if (!isEdit && selectedEntity && !filterEntity) {
      setValue('defaultEntityPage', false);
      setDefaultEntityPage(false);
    }
  }, [selectedEntity, filterEntity, isEdit, setValue]);

  return (
    <Box
      className="autofillForDarkBg"
      borderRadius="15px"
      w="full"
      border="0.5px solid #D9D9D9"
      boxShadow="0px 5px 10px rgba(0, 0, 0, 0.05)"
      overflow="hidden"
    >
      <Box bg={modeFormBoxBg} w="full" borderRadius="15px">
        <Stack
          p="14px 14px 14px 24px"
          bg={modeTextBg}
          direction="row"
          align="center"
          borderRadius="15px 15px 0 0"
        >
          <Flex direction="row" gap={1} align="center">
            <Text fontSize="18px" textStyle="headingFamilyMedium">
              Pass target
            </Text>
          </Flex>
        </Stack>
        <Divider borderColor={modeBorderColor} />

        {entities.length > 1 && (
          <>
            <FormControl borderRadius="15px" isRequired>
              <Flex alignItems="center">
                <Hide below="2xl">
                  <Box
                    pl="24px"
                    flexShrink={0}
                    w="240px"
                    borderRadius={isEdit && defaultEntityPage && '0 0 0 15px'}
                  >
                    <FormLabel m={0}>Entity</FormLabel>
                  </Box>
                </Hide>
                <Box
                  p="14px"
                  w="full"
                  bg={modeLabelBg}
                  borderRadius={
                    isEdit &&
                    defaultEntityPage && {
                      base: '0 0 15px 15px',
                      '2xl': '0 0 15px 0',
                    }
                  }
                >
                  <Show below="2xl">
                    <FormLabel>Entity</FormLabel>
                  </Show>
                  <CustomReactSelect
                    options={entitiesList}
                    placeholder="Select entity"
                    formatOptionLabel={formatOptionLabelFilterPassTemplate}
                    onChange={(entity) => {
                      setValue('byEntity', true);
                      setValue('entity', {
                        label: entity.label,
                        value: entity.value,
                      });
                      setValue('pageName', `${entity.label} page`);
                      setSelectedEntity(entity.value);
                      setSelectedPassTemplates([]);
                      trigger('entity');
                    }}
                    value={watchEntity}
                    id="passTemplateFilter"
                    passTemplate
                    aria-label="Entity"
                    variant="light"
                    isDisabled={isEdit}
                    menuPortalTarget={document.body}
                  />
                </Box>
              </Flex>
            </FormControl>
            <Divider />
          </>
        )}
        {!isEdit && (
          <Collapse in={entities.length === 1 || watchByEntity}>
            <FormControl borderRadius="15px">
              <Flex alignItems="center">
                <Hide below="2xl">
                  <Box
                    pl="24px"
                    flexShrink={0}
                    w="240px"
                    borderRadius={!isEdit && defaultEntityPage && '0 0 0 15px'}
                  >
                    <Flex direction="row" alignItems="center" gap={1}>
                      <FormLabel m={0}>Make default entity page</FormLabel>
                      <Tooltip
                        label="This page will be used automatically for all passes from the selected entity unless otherwise specified"
                        hasArrow
                        placement="top"
                        fontSize="12px"
                        bg={bgTooltip}
                        color={textTooltip}
                        borderRadius="6px"
                        textAlign="center"
                        p="10px"
                      >
                        <Box cursor="pointer" maxW="fit-content" mb={1}>
                          <CustomExclamationMarkInverseIcon
                            boxSize="12px"
                            mode="light"
                          />
                        </Box>
                      </Tooltip>
                    </Flex>
                  </Box>
                </Hide>
                <Box
                  p="14px"
                  w="full"
                  bg={modeLabelBg}
                  borderRadius={
                    !isEdit &&
                    defaultEntityPage && {
                      base: '0 0 15px 15px',
                      '2xl': '0 0 15px 0',
                    }
                  }
                >
                  <Show below="2xl">
                    <Flex direction="row" alignItems="center" gap={1}>
                      <FormLabel m={0}>Make default entity page</FormLabel>
                      <Tooltip
                        label="This page will be used automatically for all passes from the selected entity unless otherwise specified"
                        hasArrow
                        placement="top"
                        fontSize="12px"
                        bg={bgTooltip}
                        color={textTooltip}
                        borderRadius="6px"
                        textAlign="center"
                        p="10px"
                      >
                        <Box cursor="pointer" maxW="fit-content" mb={1}>
                          <CustomExclamationMarkInverseIcon
                            boxSize="12px"
                            mode="light"
                          />
                        </Box>
                      </Tooltip>
                    </Flex>
                  </Show>
                  <Tooltip
                    label="A default page already exists for this entity. Please remove it to create a new one"
                    hasArrow
                    placement="top"
                    fontSize="12px"
                    bg={bgTooltip}
                    color={textTooltip}
                    borderRadius="6px"
                    textAlign="center"
                    p="10px"
                    display={disableSwitch ? 'block' : 'none'}
                  >
                    <Box cursor="pointer" maxW="fit-content">
                      <Switch
                        id="defaultEntityPage"
                        {...register('defaultEntityPage')}
                        isChecked={watchDefaultEntityPage}
                        onChange={(e) => {
                          setValue('defaultEntityPage', e.target.checked);
                          setValue('byPassTemplate', false);
                          setValue('passTemplate', null);
                          setDefaultEntityPage(e.target.checked);
                          setSelectedPassTemplates([]);
                        }}
                        isDisabled={!watchEntity || disableSwitch}
                      />
                    </Box>
                  </Tooltip>
                </Box>
              </Flex>
            </FormControl>
            <Divider />
          </Collapse>
        )}
        {((isEdit && !watchDefaultEntityPage) || !watchDefaultEntityPage) && (
          <>
            <FormControl
              borderRadius="15px"
              isDisabled={!isEdit && watchDefaultEntityPage}
            >
              <Flex alignItems="center">
                <Hide below="2xl">
                  <Box
                    pl="24px"
                    flexShrink={0}
                    w="240px"
                    borderRadius="0 0 0 15px"
                  >
                    <Flex direction="row" alignItems="center" gap={1}>
                      <FormLabel m={0}>Pass templates</FormLabel>
                      <Tooltip
                        label="You can select one or more pass templates that will use this download page"
                        hasArrow
                        placement="top"
                        fontSize="12px"
                        bg={bgTooltip}
                        color={textTooltip}
                        borderRadius="6px"
                        textAlign="center"
                        p="10px"
                      >
                        <Box cursor="pointer" maxW="fit-content">
                          <CustomExclamationMarkInverseIcon
                            boxSize="12px"
                            mode="light"
                            mb={1}
                          />
                        </Box>
                      </Tooltip>
                    </Flex>
                  </Box>
                </Hide>
                <Box
                  p="14px"
                  w="full"
                  bg={modeLabelBg}
                  borderRadius={{
                    base: '0 0 15px 15px',
                    '2xl': '0 0 15px 0',
                  }}
                >
                  <Show below="2xl">
                    <Flex direction="row" alignItems="center" gap={1}>
                      <FormLabel m={0}>Pass templates</FormLabel>
                      <Tooltip
                        label="You can select one or more pass templates that will use this download page"
                        hasArrow
                        placement="top"
                        fontSize="12px"
                        bg={bgTooltip}
                        color={textTooltip}
                        borderRadius="6px"
                        textAlign="center"
                        p="10px"
                      >
                        <Box cursor="pointer" maxW="fit-content" mb={1}>
                          <CustomExclamationMarkInverseIcon
                            boxSize="12px"
                            mode="light"
                          />
                        </Box>
                      </Tooltip>
                    </Flex>
                  </Show>
                  <CustomSearchableSelect
                    options={passTemplatesList}
                    placeholder="Select pass templates"
                    formatOptionLabel={formatOptionLabelFilterPassTemplate}
                    onChange={(passTemplates) => {
                      if (!isEdit) {
                        setValue('byPassTemplate', true);
                        setValue('defaultEntityPage', false);
                        setValue('pageName', '');
                        trigger('passTemplate');
                      }
                      setSelectedPassTemplates(passTemplates);
                    }}
                    loadOptions={async (searchTerm) => {
                      try {
                        const { passTemplatesList } = await fetchPassTemplates(
                          authAxios,
                          isEdit ? selectedEntity : filterEntity,
                          searchTerm,
                          passDownloadCtx.passTemplates
                        );
                        return passTemplatesList;
                      } catch (error) {
                        console.error('Error loading pass templates:', error);
                        return [];
                      }
                    }}
                    value={selectedPassTemplates}
                    isMulti
                    isDisabled={!isEdit && defaultEntityPage}
                    variant="light"
                    menuPortalTarget={document.body}
                  />
                </Box>
              </Flex>
            </FormControl>
            <Divider />
          </>
        )}
      </Box>
    </Box>
  );
};

export default PassTargetSection;
